import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Alert from 'shared/components/Alert';
import TranslateError from 'shared/components/TranslateError';
import CloseButton from 'shared/components/buttons/Close';
import { useWsContext } from 'shared/contexts/WsContextProvider';
import parsePartialJson from 'shared/lib/parsePartialJson';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import { selectors } from 'userportal/ducks/auth';
import RecruiterReview from './RecruiterReview';
import styles from './UserJobReviewModal.module.scss';

const FLUSH_INTERVAL = 120; // ms; re-parse the growing JSON at most this often

const UserJobReviewModal = ({ jobId, score, scoreDims, onClose }) => {
  const { t, i18n } = useTranslation('userportal');
  const { socket } = useWsContext();

  const [review, setReview] = useState(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [injection, setInjection] = useState({ injectionDetected: false, injectionEvidence: null });
  const user = useSelector(selectors.getUser);

  // the raw (incomplete) JSON text accumulates outside of React state; only
  // the throttled parse result triggers a render
  const rawRef = useRef('');
  const flushTimer = useRef(null);

  useEffect(() => {
    const flush = () => {
      flushTimer.current = null;
      const parsed = parsePartialJson(rawRef.current);
      if (parsed) {
        setReview(parsed);
      }
    };

    const scheduleFlush = () => {
      if (!flushTimer.current) {
        flushTimer.current = setTimeout(flush, FLUSH_INTERVAL);
      }
    };

    const abortCallback = socket.callApi(
      'POST',
      '/user/jobs/job-review',
      { jobId, language: i18n.language },
      (data) => {
        if (data.warning) {
          setWarning(<TranslateError>{data.warning}</TranslateError>);
        }

        if (data.content) {
          setWarning('');
          rawRef.current += data.content;
          scheduleFlush();
        }
        if (data.complete) {
          setWarning('');
          if (flushTimer.current) {
            clearTimeout(flushTimer.current);
            flushTimer.current = null;
          }
          rawRef.current = '';
          setReview(data.complete);
          setInjection({
            injectionDetected: data.injectionDetected || false,
            injectionEvidence: data.injectionEvidence || null,
          });
          setDone(true);
        }
      },
      (err) => {
        setError(<TranslateError>{err.error}</TranslateError>);
      }
    );

    googleAnalytics.event('user_job_review', { userId: user.id, jobId, score });

    return () => {
      if (abortCallback) {
        abortCallback();
      }
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
      }
    };
  }, []);

  if (error) {
    return (
      <div className={styles.errorWrap}>
        <div className={styles.closeRow}>
          {onClose && (
            <CloseButton onClick={onClose}>{t('common:linkButton.close')}</CloseButton>
          )}
        </div>
        <Alert variant="error" canClose={false}>
          {error}
        </Alert>
      </div>
    );
  }

  // the panel renders immediately in its skeleton state — RecruiterReview
  // handles a null/partial review itself
  return (
    <>
      {warning && (
        <div className={styles.alerts}>
          <Alert variant="warning" canClose={false}>
            {warning}
          </Alert>
        </div>
      )}
      <RecruiterReview
        review={review}
        done={done}
        jobId={jobId}
        score={score}
        scoreDims={scoreDims}
        gender={user.gender}
        injectionDetected={injection.injectionDetected}
        injectionEvidence={injection.injectionEvidence}
        onClose={onClose}
      />
    </>
  );
};

UserJobReviewModal.propTypes = {
  jobId: PropTypes.string.isRequired,
  score: PropTypes.number,
  scoreDims: PropTypes.object,
  onClose: PropTypes.func,
};

UserJobReviewModal.defaultProps = {
  score: 0,
  scoreDims: null,
  onClose: null,
};

export default UserJobReviewModal;
